/**
 * svgUtils.ts
 * US-006: Safe Inline SVG Rendering in Math Coach Tutor Messages
 *
 * Provides two exports:
 *   - parseSvgSegments: splits a message string into ordered text and SVG segments
 *   - sanitiseSvg: strips all XSS vectors from an SVG string using DOMParser + allowlist
 *
 * Security model: allowlist-based (permit only explicitly known-safe elements and attributes).
 * Uses DOMParser for proper DOM tree traversal — no regex sanitisation bypasses possible.
 */

/**
 * A segment of message content — either plain text or an SVG block.
 */
export type SvgSegment =
  | { type: 'text'; value: string }
  | { type: 'svg'; value: string };

/**
 * SVG_REGEX: matches complete SVG blocks (case-insensitive, dot-all via [\s\S]).
 * Capturing group ensures String.split() keeps the matched blocks in the result array.
 */
const SVG_REGEX = /(<svg[\s\S]*?<\/svg>)/gi;

/**
 * Allowed SVG element tag names (lowercase).
 * Note: image, foreignObject, script, style are deliberately excluded.
 */
const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'path',
  'rect',
  'text',
  'tspan',
  'defs',
  'clippath',
  'marker',
  'pattern',
  'title',
  'desc',
  'use',
]);

/**
 * Allowed SVG attribute names (lowercase).
 * Note: href, xlink:href are deliberately excluded to prevent external resource fetching.
 * The style attribute is allowed as a per-element attribute (inline styles only).
 * The <style> element itself is not in ALLOWED_ELEMENTS, preventing embedded stylesheets.
 */
const ALLOWED_ATTRIBUTES = new Set([
  'viewbox',
  'width',
  'height',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'd',
  'points',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'transform',
  'class',
  'id',
  'clip-path',
  'clip-rule',
  'fill-rule',
  'marker-start',
  'marker-mid',
  'marker-end',
  'font-size',
  'font-family',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
  'dx',
  'dy',
  'rotate',
  'patternunits',
  'patterncontentunits',
  'patterntransform',
  'markerunits',
  'markerwidth',
  'markerheight',
  'orient',
  'refx',
  'refy',
  'gradientunits',
  'gradienttransform',
  'spreadmethod',
  'style',
]);

/**
 * parseSvgSegments
 *
 * Splits a message content string into an ordered array of text and SVG segments.
 * Preserves the original interleaving order: text, SVG, text, SVG, text...
 *
 * @param content - The raw message content string
 * @returns Ordered array of SvgSegment objects
 *
 * Edge cases:
 * - No SVG in content → returns single text segment
 * - Content is only SVG → returns single SVG segment
 * - Multiple consecutive SVGs → each is its own SVG segment
 * - Empty string → returns empty array
 */
export function parseSvgSegments(content: string): SvgSegment[] {
  if (!content) return [];

  // Split using capturing group — keeps matched SVG blocks in the result array
  const parts = content.split(SVG_REGEX);
  const segments: SvgSegment[] = [];

  for (const part of parts) {
    if (!part) continue; // skip empty strings between consecutive SVGs

    // A captured SVG segment from split() will both start with <svg and end with </svg>.
    // Checking both guards against text that incidentally contains '<svg' but is not a complete block.
    if (/^<svg/i.test(part) && /<\/svg>\s*$/i.test(part)) {
      // SVG segment: trim surrounding whitespace (AI often adds newlines before/after)
      segments.push({ type: 'svg', value: part.trim() });
    } else {
      // Text segment: preserve as-is (do NOT trim — preserves newlines for whitespace-pre-wrap)
      if (part.length > 0) {
        segments.push({ type: 'text', value: part });
      }
    }
  }

  return segments;
}

/**
 * sanitiseSvg
 *
 * Sanitises an SVG string using DOMParser + allowlist approach.
 * Removes all XSS vectors: script elements, foreignObject, image (external fetch),
 * on* event handlers, javascript: URLs, style elements.
 * Normalises width="100%" and height="auto" on the root <svg> element for responsive display.
 *
 * Returns empty string '' if:
 * - Input is empty or blank
 * - DOMParser reports a parse error (malformed XML)
 * - Root element is not an SVG element
 *
 * @param rawSvg - The raw SVG string to sanitise
 * @returns Sanitised SVG string, or '' for invalid/unsafe input
 */
export function sanitiseSvg(rawSvg: string): string {
  if (!rawSvg || rawSvg.trim() === '') return '';

  // Step 1: Parse using the browser's built-in DOMParser (proper DOM tree — no regex bypass)
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawSvg, 'image/svg+xml');

  // Step 2: Check for parse errors — DOMParser inserts <parsererror> on failure
  if (doc.querySelector('parsererror')) return '';

  // Step 3: Verify root element is <svg>
  const svgEl = doc.documentElement;
  if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg') return '';

  // Step 4: Collect all elements (root + all descendants)
  const allElements = [svgEl, ...Array.from(svgEl.querySelectorAll('*'))];

  // Step 5: Walk in reverse order — removing a child does not break forward iteration
  // when walking in reverse, since we've already collected the array.
  for (let i = allElements.length - 1; i >= 0; i--) {
    const element = allElements[i];
    const tagName = element.tagName.toLowerCase();

    // 5a: Check element tag against allowlist
    if (!ALLOWED_ELEMENTS.has(tagName)) {
      element.parentNode?.removeChild(element);
      continue; // no attribute processing needed — element is removed
    }

    // 5b: Sanitise attributes on allowed elements
    const attributesToRemove: string[] = [];

    for (const attr of Array.from(element.attributes)) {
      const attrName = attr.name.toLowerCase();
      const attrValue = attr.value;

      // Remove all event handler attributes (on*)
      if (/^on/i.test(attrName)) {
        attributesToRemove.push(attr.name);
        continue;
      }

      // Remove attributes with javascript: URL values
      if (/^\s*javascript:/i.test(attrValue)) {
        attributesToRemove.push(attr.name);
        continue;
      }

      // Remove attributes not in the allowlist
      if (!ALLOWED_ATTRIBUTES.has(attrName)) {
        attributesToRemove.push(attr.name);
      }
    }

    for (const attrName of attributesToRemove) {
      element.removeAttribute(attrName);
    }
  }

  // Step 6: Normalise root SVG sizing for responsive display
  // width="100%" fills the message bubble; height="auto" + viewBox scales proportionally
  svgEl.setAttribute('width', '100%');
  svgEl.setAttribute('height', 'auto');

  // Step 7: Serialise back to string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(svgEl);
}
